import { HeaderText } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { removeEmptyKeys } from "@/lib/utils";
import { useRouter, useSearch } from "@tanstack/react-router";
import { format } from "date-fns";
import type { z } from "zod";
import { PatientSearchForm } from "./PatientSearchForm";
import { PatientTable } from "./PatientTable";
import { VisitSearchForm } from "./VisitSearchForm";
import { usePatientSearch } from "./network/usePatientSearch";
import type { patientSearchFormSchema, visitSearchFormSchema } from "./schemas";

export const Search = () => {
  const { navigate, state } = useRouter();
  const { visitId, patientId, patientFirstName, patientLastName, dob } =
    useSearch({
      from: "/search",
    });

  const {
    data: patientsData,
    isFetching: isFetchingPatients,
    isFetched: isFetchedPatients,
    isError: isErrorPatients,
  } = usePatientSearch({
    visitId,
    patientId,
    patientFirstName,
    patientLastName,
    dob,
  });

  const onSubmitPatient = (values: z.infer<typeof patientSearchFormSchema>) => {
    trackEvent(TRACKERS.SEARCH_BY_PATIENT);
    const search = removeEmptyKeys({
      patientId: values.id,
      patientFirstName: values.firstName,
      patientLastName: values.lastName,
      dob: values.dob ? format(new Date(values.dob), "yyyy-MM-dd") : undefined,
    });

    navigate({
      to: state.location.pathname,
      search,
    });
  };

  const onSubmitVisit = (values: z.infer<typeof visitSearchFormSchema>) => {
    trackEvent(TRACKERS.SEARCH_BY_VISIT);
    navigate({
      to: state.location.pathname,
      search: {
        visitId: values.id,
      },
    });
  };

  const isLoadingPatientSearch = isFetchingPatients && !visitId;
  const isLoadingVisitSearch = isFetchingPatients && !!visitId;

  return (
    <div className="p-6 h-full overflow-auto pb-12">
      <HeaderText size="xl" className="mb-4">
        Search
      </HeaderText>

      <section className="flex gap-3 flex-col lg:flex-row">
        <div className="space-y-2">
          <HeaderText>By Patient</HeaderText>
          <PatientSearchForm
            onSubmit={onSubmitPatient}
            isLoading={isLoadingPatientSearch}
          />
        </div>

        <div className="space-y-2 lg:border-l lg:border-primary lg:pl-3">
          <HeaderText>By Visit</HeaderText>
          <VisitSearchForm
            onSubmit={onSubmitVisit}
            isLoading={isLoadingVisitSearch}
          />
        </div>
      </section>

      {(isFetchedPatients || isFetchingPatients) && (
        <section className="mt-4">
          <PatientTable
            patients={patientsData?.results || []}
            isLoading={isFetchingPatients}
            isError={isErrorPatients}
            includeVisitRfe={!!visitId}
            visitId={visitId}
          />
        </section>
      )}
    </div>
  );
};
